import React, { useState, useMemo } from "react";
import { Modal, Input, Tree, Row, Col, Typography } from "antd";
import * as Icons from "@ant-design/icons";
import { useMenu } from "../../store/context/admin/menu-context";
import { useLanguage } from "../../store/context/language-context";
import { useNavigate } from "react-router-dom"; // Add this import
import { useTranslation } from "react-i18next";

const { Search } = Input;
const { Title } = Typography;

const BankLingoTreeGrid = ({ searchTerm = "", onNavigate }) => {
  const { menuList: data } = useMenu();

  const { t } = useTranslation();

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? React.createElement(IconComponent) : null;
  };

  const highlightText = (text, search) => {
    if (!search) return text;

    const regex = new RegExp(`(${search})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#fff3cd", // reddish yellow background
            color: "#856404",
            padding: "2px 4px",
            borderRadius: "3px",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Color scheme for different module types

  const generateTreeData = () => {
    const modules = (data?.modules ?? []).map((module) => ({
      title: (
        <span
          style={{
            fontWeight: "bold",
            // color: module.navigationColor,
            marginLeft: "8px",
          }}
        >
          {highlightText(
            t(module.translationCode) || t(module.name),
            searchTerm
          )}
        </span>
      ),
      key: `module-${module.code}`,
      path: module.path, // Add path to module
      icon: (
        <div
          style={{
            backgroundColor: module.navigationColor,
            color: "white",
            padding: "6px 6px",
            borderRadius: "3px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            fontWeight: "bold",
            minWidth: "24px",
            height: "24px",
          }}
        >
          {getIcon(module.icon) || module.code?.charAt(0)?.toUpperCase()}
        </div>
      ),
      children: data.menus
        .filter((menu) => menu.menu_groups?.includes(module.code))
        .map((menu) => ({
          title: (
            <span
              style={{
                fontSize: "14px",
                marginLeft: "4px",
              }}
            >
              {highlightText(
                t(menu.translationCode) || t(menu.label),
                searchTerm
              )}
            </span>
          ),
          key: `menu-${menu.code}`,
          path: menu.path, // Add path to menu
          icon: getIcon(menu.icon),
          children:
            menu.submenus?.map((submenu) => ({
              title: (
                <span
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    marginLeft: "4px",
                  }}
                >
                  {highlightText(
                    t(submenu.translationCode) || t(submenu.label),
                    searchTerm
                  )}
                </span>
              ),
              key: `submenu-${submenu.code}`,
              path: submenu.path, // Add path to submenu
              icon: getIcon(submenu.icon),
              isLeaf: true,
            })) || [],
        })),
    }));

    return modules;
  };

  // Handle tree item selection
  const handleSelect = (selectedKeys, { node }) => {
    if (node.path && onNavigate) {
      onNavigate(node.path);
    }
  };

  const treeData = generateTreeData();
  const itemsPerColumn = Math.ceil(treeData.length / 4);

  const columns = [
    treeData.slice(0, itemsPerColumn),
    treeData.slice(itemsPerColumn, itemsPerColumn * 2),
    treeData.slice(itemsPerColumn * 2, itemsPerColumn * 3),
    treeData.slice(itemsPerColumn * 3),
  ];

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#ffffff",
      }}
    >
      <Row gutter={[16, 16]}>
        {columns.map((columnData, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <div
              style={{
                padding: "10px",
                borderRadius: "8px",
                minHeight: "400px",
              }}
            >
              <Tree
                showIcon
                defaultExpandAll
                treeData={columnData}
                onSelect={handleSelect}
                style={{
                  fontSize: "13px",
                }}
                className="compact-tree"
              />
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Updated Modal component with custom styles
const NavigatorModal = ({ isNavigatorOpen, setIsNavigatorOpen }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate(); // Add navigation hook

  // Handle navigation and close modal
  const handleNavigate = (path) => {
    navigate(path);
    setIsNavigatorOpen(false); // Close modal after navigation
  };

  return (
    <>
      <style>
        {`
                .compact-tree .ant-tree-node-content-wrapper {
                    padding: 2px 4px !important;
                    margin: 1px 0 !important;
                    cursor: pointer !important;
                }
                
                .compact-tree .ant-tree-indent-unit {
                    width: 16px !important;
                }
                
                .compact-tree .ant-tree-switcher {
                    width: 16px !important;
                    height: 20px !important;
                }
                
                .compact-tree .ant-tree-iconEle {
                    margin-right: 4px !important;
                }
                
                .compact-tree .ant-tree-title {
                    font-size: 13px !important;
                }
                
                .compact-tree .ant-tree-node-content-wrapper:hover {
                    background-color: #f0f0f0 !important;
                }
                
                .compact-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper {
                    background-color: #e6f7ff !important;
                }
                `}
      </style>
      <Modal
        width={1200}
        open={isNavigatorOpen}
        closable={false}
        onCancel={() => setIsNavigatorOpen(false)}
        footer={null}
        title={
          <Search
            placeholder="Search in navigator..."
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
        }
        style={{
          top: 48, // Fixed top position
          height: "calc(100vh - 48px)", // Full height minus top offset
          maxHeight: "calc(100vh - 48px)",
          paddingBottom: 0,
        }}
        bodyStyle={{
          height: "calc(100vh - 48px - 55px)", // Subtract header height
          maxHeight: "calc(100vh - 48px - 55px)",
          overflow: "auto",
          padding: 0,
        }}
      >
        <BankLingoTreeGrid
          searchTerm={searchTerm}
          onNavigate={handleNavigate}
        />
      </Modal>
    </>
  );
};

export default NavigatorModal;
